/**
 * The ch05 feature manifest — end of season and exit.
 *
 * Six routes, and the split is docs/05 §7's own:
 *
 * | Route | Capability | The question it answers |
 * |---|---|---|
 * | `plans` | X1 | Which options are on plan for terminal stock, and which are not — `r*` against `r̂` |
 * | `plans/unsigned` | X1 | Which plans still wait on a merchant or finance signature |
 * | `plans/terminal-check` | X1 | What if the residual lands at N — the terminal-stock what-if |
 * | `plans/terminal-position` | X1 | The measured terminal position, per cluster and week |
 * | `cadence` | X2 | What are we repricing this week, why, and what does holding cost instead |
 * | `cadence/required-rate` | X2 | What rate a position would need — the what-if |
 * | `cadence/overrides` | X2 | How often the recommendation was taken, per reason code |
 * | `consolidation` | X3 | Which doors are we consolidating into, what does the lane cost, which runs does it repair |
 *
 * Since the client's rules 1 and 3 (DEC-84, DEC-88), a screen that held several functions is
 * several pages, each a rail sub-entry declared as its navigation entry's `children`:
 * `position` · `stranded` (`docs/adr/0068`), and `consolidation` · `consolidation-doors` ·
 * `consolidation-lanes` · `lane-whatif` (`docs/adr/7001`), amendments of 2026-09-30 (Lanes left the
 * sub-entries with the owner ruling of 2026-11-02: the lane book is history since the one run). The
 * consolidation siblings are sibling PATHS because `consolidation/:storeKey` owns every second
 * segment under `consolidation/` (`door-drill.ts`).
 * | `handover` | X6 | Will each cluster be clear by its floor-set date — **the go/no-go** |
 * | `routes` | X4, X5 | For each lot at its floor, what is every route worth |
 * | `returns` | X7 | Where did graded units go, and how much realisation did each grade recover |
 *
 * `plans` is first because everything else is measured against it. reqs/05 §3.1 makes the
 * Terminal Stock Ratio the controlled quantity and every other capability a means to it —
 * the cadence moves units, consolidation makes the remaining units saleable, routing
 * disposes of what the cadence cannot clear, carry-over declines to dispose, and the
 * handover calendar sets the date all four are measured against. A planner who has not
 * seen the plan cannot judge any of the other five screens.
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than census
 * failures later.
 *
 * The drill targets follow reqs/05's own object chain rather than convenience: a plan
 * position drills to the option's cadence proposals and cross-drills to the routes that
 * bound its price floor, because reqs/05 §3.2 rule 7 makes the floor the thing the cadence
 * refuses to walk below and X4 the thing that computes it — one number, two screens.
 */

import { defineFeature } from '../manifest';
import { EXIT_GUIDES } from './guides';
import {
  lifecycleEvidenceExport, lifecycleOutletEvidenceExport, lifecycleOutletExport, lifecycleOutletTransfersExport,
  lifecyclePhaseHistoryExport, lifecyclePhasesExport, lifecycleTransitionsExport, lifecycleWithdrawalsExport,
} from './lifecycle/exports';

import {
  cadenceExport,
  cadenceProposalExport,
  consolidationExport,
  consolidationRunDoorsExport,
  consolidationRunLanesExport,
  doorLanesExport,
  evidenceExport,
  exitPlanDetailExport,
  exitPlanExport,
  handoverExport,
  overrideRateExport,
  lifecycleExport,
  positionExport,
  terminalPositionExport,
  unsignedPlansExport,
  returnsExport,
  routingExport,
} from './exports';

export default defineFeature({
  slug: 'exit',
  titleKey: 'exit.title',
  navLabelKey: 'exit.navLabel',
  icon: 'percent',  // brand guide p.14
  railGroup: 'active',  // bata-ui#132 — a module planners run decisions in today
  localeNamespace: 'exit',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: EXIT_GUIDES,
  // Six working screens in the order a planner reads them — the estate position first,
  // then the plan they are measured against, then the four capabilities that move units
  // — and the one entry still to come last. Every screen except `evidence` now reads
  // from the pinned client, so there is no longer a working half and an inert half to
  // keep apart: the nav is the chapter's own sequence again.
  navigation: [
    {
      labelKey: 'exit.nav.position',
      to: '/exit/position',
      order: 50,
      children: [
        { labelKey: 'exit.nav.positionBook', to: '/exit/position', order: 1 },
        { labelKey: 'exit.nav.stranded', to: '/exit/stranded', order: 2 },
      ],
    },
    // One function per page (client rule 3, 2026-09-30; DEC-88): the plan book and the cadence
    // queue each keep their path as their first view, and what shared the page is a sub-entry of
    // its own. Pricing draws them in the rail (ADR 9023's amendment of 2026-09-30).
    {
      labelKey: 'exit.nav.plans',
      to: '/exit/plans',
      order: 51,
      children: [
        { labelKey: 'exit.nav.plansRegister', to: '/exit/plans', order: 1 },
        { labelKey: 'exit.nav.plansUnsigned', to: '/exit/plans/unsigned', order: 2 },
        { labelKey: 'exit.nav.plansTerminalCheck', to: '/exit/plans/terminal-check', order: 3 },
        { labelKey: 'exit.nav.plansTerminalPosition', to: '/exit/plans/terminal-position', order: 4 },
      ],
    },
    {
      labelKey: 'exit.nav.cadence',
      to: '/exit/cadence',
      order: 52,
      children: [
        { labelKey: 'exit.nav.cadenceQueue', to: '/exit/cadence', order: 1 },
        { labelKey: 'exit.nav.cadenceRequiredRate', to: '/exit/cadence/required-rate', order: 2 },
        { labelKey: 'exit.nav.cadenceOverrides', to: '/exit/cadence/overrides', order: 3 },
      ],
    },
    // «Clearance consolidation» is no navigation entry since bata-platform#951 (owner rulings 2026-11-03/04):
    // the pilot consolidation its Estate and Doors read is retired and their addresses open Compacting ›
    // Consolidation, the one move run's; Lane what-if, Lanes and Simulate are the retired lane book's history,
    // reached only by the links that name them.
    { labelKey: 'exit.nav.handover', to: '/exit/handover', order: 54 },
    { labelKey: 'exit.nav.routes', to: '/exit/routes', order: 55 },
    { labelKey: 'exit.nav.returns', to: '/exit/returns', order: 56 },
    { labelKey: 'exit.nav.evidence', to: '/exit/evidence', order: 57, disabled: true },
    // End of life — three entries since bata-reqs issue 38 R1 (owner rulings 2026-10-17,
    // `docs/adr/9064`), no «Fine vita» level above them. The monthly MAIN → ZONA_SALES transition
    // and the forced-donor withdrawal it sets off (issue 35 P5, `docs/adr/7003`); the ZONA_SALES →
    // OUTLET exit and its transfer (P4, `docs/adr/7004`); the phase register (P5, bata-platform ADR
    // 7031, `docs/adr/7005`). Each move entry's «Parametri» is the rail's own sub-entry
    // (`src/features/rail-apps.ts`), not a child here: the register is ch00's route.
    {
      labelKey: 'exit.nav.lifecycleTransition',
      to: '/exit/lifecycle',
      order: 58,
      children: [
        { labelKey: 'exit.nav.lifecycleRecommendations', to: '/exit/lifecycle', order: 1 },
        { labelKey: 'exit.nav.lifecycleWithdrawals', to: '/exit/lifecycle-withdrawals', order: 2 },
      ],
    },
    {
      labelKey: 'exit.nav.lifecycleOutlet',
      to: '/exit/lifecycle-outlet',
      order: 59,
      children: [
        { labelKey: 'exit.nav.lifecycleRecommendations', to: '/exit/lifecycle-outlet', order: 1 },
        { labelKey: 'exit.nav.lifecycleOutletTransfers', to: '/exit/lifecycle-outlet-transfers', order: 2 },
      ],
    },
    { labelKey: 'exit.nav.lifecyclePhases', to: '/exit/lifecycle-phases', order: 60 },
  ],
  routes: [
    {
      // The chapter's front door. docs/05 §7 gives this chapter six routes and no
      // landing surface, and six routes with no front door is how a planner bookmarks
      // one and ignores five. POSITION: the exit book and terminal stock by lifecycle
      // class. `tile` for the book's figures, `table` for the classes.
      path: 'position',
      titleKey: 'exit.position.title',
      urlParams: ['market', 'season'],
      component: () => import('./screens/PositionScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.position.lifecycle',
        formats: ['csv', 'xlsx'],
        resolve: lifecycleExport,
      },
      // A class's worst option opens its plan's page, on the plan's own key (bata-ui#213: an
      // option key never addressed a plan — an option carries one per season).
      drill: {
        to: '/exit/plans/:exitPlanId',
        grain: 'article',
      },
    },
    {
      // STRANDED — the position screen's second function, a page of its own (client rule 3,
      // `docs/adr/0068` amendment of 2026-09-30). `tile` and `table`, and deliberately no
      // `chart`: the stranded-lot count is a count of three joined predicates, and a picture
      // of three counts is a table with extra steps.
      path: 'stranded',
      titleKey: 'exit.stranded.title',
      urlParams: ['market', 'season'],
      component: () => import('./screens/StrandedScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.stranded.table',
        formats: ['csv', 'xlsx'],
        resolve: positionExport,
      },
      // A stranded lot drills to the three screens that each hold one third of the
      // reason it is stranded. The plan is the target because it is the register the
      // join is built on; the other two are the evidence. One lot per plan, so the row drills
      // on the plan's own key (the #203 audit: it said `:optionKey`, which nothing mounted).
      drill: {
        to: '/exit/plans/:exitPlanId',
        grain: 'article',
        alsoTo: ['/exit/cadence', '/exit/routes'],
      },
    },
    {
      // `tile` is gone from the surfaces and the stat tiles with it. A tile per plan row
      // is a wall of tiles at any real book size, and the three this screen used to slice
      // off the front were a selection nobody declared. The tiles live on `/exit/position`,
      // one per lifecycle class, with a stated rule. ADR 0085: `surfaces` declares intent.
      // `chart` went with template E (Package C6): the Register is a list, its table the content.
      path: 'plans',
      titleKey: 'exit.plans.title',
      urlParams: ['exitPlanId', 'market', 'season'],
      component: () => import('./screens/ExitPlanScreen'),
      surfaces: ['table'],
      exports: {
        id: 'exit.plans.table',
        formats: ['csv', 'xlsx'],
        resolve: exitPlanExport,
      },
      // Keyed on `exit_plan_id`, which is what `GET /exit/plans/{exit_plan_id}` is keyed on: an
      // option can carry a plan per season (`scope.ts`). `?exitPlanId=` redirects here.
      drill: {
        to: '/exit/plans/:exitPlanId',
        grain: 'article',
        alsoTo: ['/exit/cadence', '/exit/handover'],
      },
    },
    {
      // UNSIGNED — the sign-off gap list (reqs/05 §8.3). A static segment: the router resolves it
      // before the plan drill `plans/:exitPlanId` (`resolveRoute`, static first).
      path: 'plans/unsigned',
      titleKey: 'exit.unsigned.pageTitle',
      urlParams: ['market', 'season'],
      component: () => import('./screens/ExitPlansUnsignedScreen'),
      surfaces: ['table'],
      exports: { id: 'exit.plans.unsigned', formats: ['csv', 'xlsx'], resolve: unsignedPlansExport },
      drill: { to: '/exit/plans/:exitPlanId', grain: 'article' },
    },
    {
      // TERMINAL CHECK — the terminal-stock what-if, a form over the plan book's rows.
      path: 'plans/terminal-check',
      titleKey: 'exit.terminalStock.pageTitle',
      urlParams: ['exitPlanId', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/ExitPlansTerminalCheckScreen'),
      surfaces: ['form'],
    },
    {
      // TERMINAL POSITION — the measured terminal-stock ratio, per cluster and week.
      path: 'plans/terminal-position',
      titleKey: 'exit.terminal.pageTitle',
      urlParams: ['market', 'season'],
      component: () => import('./screens/ExitPlansTerminalPositionScreen'),
      surfaces: ['table'],
      exports: { id: 'exit.plans.terminalPosition', formats: ['csv', 'xlsx'], resolve: terminalPositionExport },
      drill: {
        terminal: true,
        reason: 'One option in one cluster in one week is the finest grain the terminal-stock read publishes, and each is a row of this page.',
      },
    },
    {
      // REQUIRED RATE — the cadence what-if, a form. A static segment: the router resolves it
      // before the proposal drill `cadence/:proposalId` (`resolveRoute`, static first).
      path: 'cadence/required-rate',
      titleKey: 'exit.requiredRate.pageTitle',
      urlParams: ['exitPlanId', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/CadenceRequiredRateScreen'),
      surfaces: ['form'],
    },
    {
      // OVERRIDE HISTORY — the disposition counts per reason code, as published.
      path: 'cadence/overrides',
      titleKey: 'exit.overrideRate.pageTitle',
      urlParams: ['market', 'season'],
      component: () => import('./screens/CadenceOverridesScreen'),
      surfaces: ['table'],
      exports: { id: 'exit.cadence.overrides', formats: ['csv', 'xlsx'], resolve: overrideRateExport },
      drill: {
        terminal: true,
        reason: 'A reason code × disposition count is the finest grain the override-rate read publishes; the proposals behind it are not named by it.',
      },
    },
    {
      // One plan, by its identifier. Reached from the plan book, the gap list and the stranded
      // lots on the position screen — every row is the drill — not from the rail.
      path: 'plans/:exitPlanId',
      titleKey: 'exit.plan.title',
      urlParams: ['exitPlanId', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/ExitPlanDetailScreen'),
      surfaces: ['tile'],
      exports: { id: 'exit.plans.detail', formats: ['csv', 'xlsx'], resolve: exitPlanDetailExport },
      drill: {
        terminal: true,
        reason: 'One exit plan record is the finest grain the plan book publishes: GET /exit/plans/{exit_plan_id} returns the record and nothing beneath it.',
      },
    },
    {
      path: 'cadence',
      titleKey: 'exit.cadence.title',
      urlParams: ['market', 'row', 'season'],
      component: () => import('./screens/CadenceScreen'),
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'exit.cadence.table',
        formats: ['csv', 'xlsx'],
        resolve: cadenceExport,
      },
      drill: {
        to: '/exit/cadence/:proposalId',
        grain: 'cluster',
        // The floor a proposal refuses to walk below is computed on the routing screen.
        alsoTo: ['/exit/routes', '/exit/plans'],
      },
    },
    {
      // One proposal, served by the queue read narrowed with `proposal_id`. Reached from the
      // queue's rows, not from the rail.
      path: 'cadence/:proposalId',
      titleKey: 'exit.proposal.title',
      urlParams: ['exitPlanId', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/CadenceProposalScreen'),
      surfaces: ['tile'],
      exports: { id: 'exit.cadence.proposal', formats: ['csv', 'xlsx'], resolve: cadenceProposalExport },
      drill: {
        terminal: true,
        reason: 'One rung-change proposal is the finest grain the cadence queue publishes: it is one option in one cluster on one as-of date, with nothing beneath it.',
      },
    },
    {
      // ESTATE's old address (bata-platform#951, owner ruling 2026-11-03): the pilot consolidation it read is
      // retired, so it opens Compacting › Consolidation, the one move run's, in place.
      path: 'consolidation',
      titleKey: 'exit.consolidation.title',
      urlParams: [],
      component: () => import('./screens/ExitConsolidationRedirectScreen'),
      surfaces: [],
    },
    {
      // DOORS's old address — the same, in place (bata-platform#951).
      path: 'consolidation-doors',
      titleKey: 'exit.consolidationDoors.title',
      urlParams: [],
      component: () => import('./screens/ExitConsolidationRedirectScreen'),
      surfaces: [],
    },
    {
      // LANES — one option's lane book, what it is worth, and the decision over it.
      path: 'consolidation-lanes',
      titleKey: 'exit.consolidationLanes.title',
      urlParams: ['exitPlanId', 'isProposed', 'laneCostAbsent', 'optionId', 'repairsSizeRun', 'routingDecisionId', 'runId'],
      component: () => import('./screens/ConsolidationLanesScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.consolidation.table',
        formats: ['csv', 'xlsx'],
        resolve: consolidationExport,
      },
      drill: {
        terminal: true,
        reason: 'A lane — source door, destination door and size of one option — is the finest grain the consolidation book publishes; it is decided over its reason-code group on this page, and its doors are opened from the Doors page.',
      },
    },
    {
      // RUN DOORS — every door position of the run, with no option chosen (ADR 9069). Opened by the cards that count
      // the whole run (the Estate's and the Cockpit's broken positions), not from the rail. `runIsBroken` and
      // `sellThroughAbsent` are the platform's own filters on the doors read (ADR 6194).
      path: 'consolidation-run-doors',
      titleKey: 'exit.consolidationRunDoors.title',
      urlParams: ['exitPlanId', 'optionId', 'routingDecisionId', 'runId', 'runIsBroken', 'sellThroughAbsent'],
      component: () => import('./screens/ConsolidationRunDoorsScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.consolidation.runDoors',
        formats: ['csv', 'xlsx'],
        resolve: consolidationRunDoorsExport,
      },
      drill: {
        to: '/exit/consolidation/:storeKey',
        grain: 'door',
        alsoTo: ['/exit/consolidation-doors'],
      },
    },
    {
      // RUN LANES — every lane of the run, proposed and withheld, with no option chosen (ADR 9069). Opened by Strategic ▸
      // Impact's «priced lanes», not from the rail. `isProposed`, `lanePriced`, `laneCostAbsent` and `repairsSizeRun` are the
      // platform's own filters on the lines read (ADR 6194).
      path: 'consolidation-run-lanes',
      titleKey: 'exit.consolidationRunLanes.title',
      urlParams: ['exitPlanId', 'isProposed', 'laneCostAbsent', 'lanePriced', 'optionId', 'repairsSizeRun', 'routingDecisionId', 'runId'],
      component: () => import('./screens/ConsolidationRunLanesScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.consolidation.runLanes',
        formats: ['csv', 'xlsx'],
        resolve: consolidationRunLanesExport,
      },
      drill: {
        to: '/exit/consolidation-lanes',
        grain: 'article',
      },
    },
    {
      // LANE WHAT-IF — one hypothetical lane priced by the planner (`docs/adr/0069`). A
      // calculator: it reads no published aggregate, so it declares no export.
      path: 'lane-whatif',
      titleKey: 'exit.laneWhatIf.title',
      urlParams: ['exitPlanId', 'lane', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/LaneWhatIfScreen'),
      surfaces: ['form'],
    },
    {
      // SIMULATE (owner ruling 2026-10-14, bata-platform ADR 7037): ask for a consolidation over
      // the pilot's doors or every door, and the market's simulations below. A sibling PATH, for
      // the reason above: `consolidation/:storeKey` owns every second segment under it.
      path: 'consolidation-simulate',
      titleKey: 'exit.consolidationSimulate.title',
      // `?exitSimulation=`: the simulation launched from here — the form stays (owner ruling 2026-10-20).
      urlParams: [],
      component: () => import('./screens/ConsolidationSimulateScreen'),
      surfaces: ['form'],
      capability: { kind: 'exit_simulation', action: 'read' },
    },
    {
      // One simulation's status, polled until it ends, and the link to its consolidation run.
      path: 'consolidation-simulate/:simulationId',
      titleKey: 'exit.consolidationSimulation.title',
      // The link to the simulated run names `?runId=` (and the door drill's `?optionId=`) — `door-drill.ts`.
      // «Modifica e rilancia» writes `?exitSimulation=` into the compose page's address.
      urlParams: ['exitSimulation', 'optionId', 'runId'],
      component: () => import('./screens/ConsolidationSimulationScreen'),
      surfaces: ['detail'],
      capability: { kind: 'exit_simulation', action: 'read' },
    },
    {
      // One door in the run: every option's position at it, the lanes leaving it and the lanes
      // arriving. Reached from the consolidation screen's door column, not from the rail.
      path: 'consolidation/:storeKey',
      titleKey: 'exit.door.title',
      urlParams: ['exitPlanId', 'lane', 'optionId', 'routingDecisionId', 'runId'],
      component: () => import('./screens/ConsolidationDoorScreen'),
      surfaces: ['table'],
      exports: { id: 'exit.consolidation.door', formats: ['csv', 'xlsx'], resolve: doorLanesExport },
      drill: {
        terminal: true,
        reason: 'A door is the finest grain the consolidation book publishes; its options link back to the consolidation screen scoped to that option, and its lanes are rows of this page.',
      },
    },
    {
      // `chart` is gone from the surfaces and so are `png` and `svg`. The weeks-to-clear
      // histogram this screen used to draw was a distribution per DOOR, and
      // `HandoverReadinessSummary` publishes one row per plan × option × cluster and no
      // door-level projection at all — so the form had nothing to bucket. ADR 0085:
      // `surfaces` declares intent, and declaring a chart nothing mounts is the same
      // defect as mounting one nothing declares.
      path: 'handover',
      titleKey: 'exit.handover.title',
      urlParams: ['market', 'season'],
      component: () => import('./screens/HandoverScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.handover.table',
        formats: ['csv', 'xlsx'],
        resolve: handoverExport,
      },
      drill: {
        to: '/exit/handover/:clusterId',
        grain: 'cluster',
        alsoTo: ['/exit/consolidation'],
      },
    },
    {
      path: 'routes',
      titleKey: 'exit.routes.title',
      urlParams: ['exitPlanId', 'market', 'optionId', 'routingDecisionId', 'runId', 'season'],
      component: () => import('./screens/RoutingScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.routes.table',
        formats: ['csv', 'xlsx'],
        resolve: routingExport,
      },
      drill: {
        to: '/exit/routes/:lotId',
        grain: 'article',
        alsoTo: ['/exit/plans', '/exit/returns'],
      },
    },
    {
      path: 'returns',
      titleKey: 'exit.returns.title',
      urlParams: ['market', 'realisationAbsent', 'season'],
      component: () => import('./screens/ReturnsScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.returns.table',
        formats: ['csv', 'xlsx'],
        resolve: returnsExport,
      },
      // The next level down is the individual return event, which is a store-operations
      // record rather than a planning object and has no screen in this product. Declared
      // terminal with the reason, because G2 test f counts undocumented terminals.
      drill: {
        terminal: true,
        reason:
          'A single return event is a store-operations record in the POS returns feed, not a planning object. The disposition, its grade and its segregation flag are the finest grain this product plans at; below it the trail continues in the returns system.',
      },
    },
    {
      // Gate D4's stored evidence and the 05.P2 shadow-season counter. `chart` is
      // deliberately NOT among the surfaces: every §7.5 form needs a series, and while
      // the gate is inadmissible a series here would be a picture of a measurement
      // nobody took. See docs/adr/0063.
      path: 'evidence',
      titleKey: 'exit.evidence.title',
      urlParams: ['market', 'season'],
      component: () => import('./screens/EvidenceScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'exit.evidence.table',
        formats: ['csv', 'xlsx'],
        resolve: evidenceExport,
      },
      // The option × cluster × shadow season is the grain the comparison is stored at
      // (`plan.exit_shadow_comparison`'s primary key). There is no level below it: the
      // gate is evaluated on the pair, not on a transaction.
      drill: {
        terminal: true,
        reason:
          'The shadow-versus-incumbent comparison is stored at option × cluster × shadow season, which is the primary key of plan.exit_shadow_comparison and the grain gate D4 is evaluated at. There is no finer level to drill to: below it lies the markdown variant that produced the comparison, which is a model artefact rather than a planning object.',
      },
    },
    {
      // TRANSITION TO END OF LIFE — the month's MAIN → ZONA_SALES recommendations for one market
      // (bata-reqs issue 35 P5, `docs/adr/7003`; bata-platform ADR 7026). Market and month on the
      // link, neither defaulted; one run of the month optional, the month's newest otherwise.
      path: 'lifecycle',
      titleKey: 'exit.lifecycle.transitions.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/TransitionsScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.transitions', formats: ['csv', 'xlsx'], resolve: lifecycleTransitionsExport },
      drill: {
        to: '/exit/lifecycle/:recommendationId',
        grain: 'article',
        alsoTo: ['/exit/lifecycle-withdrawals'],
      },
    },
    {
      // WITHDRAWAL — what the month's forced-donor withdrawal did with each accepted move (ADR 7027).
      // A sibling PATH, not `lifecycle/withdrawals`: `lifecycle/:recommendationId` owns the second
      // segment, as `consolidation/:storeKey` does for the consolidation siblings.
      path: 'lifecycle-withdrawals',
      titleKey: 'exit.lifecycle.withdrawals.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/WithdrawalsScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.withdrawals', formats: ['csv', 'xlsx'], resolve: lifecycleWithdrawalsExport },
      // A row opens its stores and its lane-book lines on this page (`?article=`), with the exit
      // lane decision over them: Consolidation ▸ Lanes cannot open a withdrawal run (ADR 7003).
      drill: {
        terminal: true,
        reason: 'An accepted move opens on this page (?article=): every store the withdrawal named and its lane-book lines, one per source, destination and size, which is the finest grain GET /exit/consolidation/lines publishes; the lines are decided here with the exit lane decision.',
      },
    },
    {
      // One recommendation: every signal read, every door, and the planner's answer. Reached from
      // the transitions page's rows, not from the rail.
      path: 'lifecycle/:recommendationId',
      titleKey: 'exit.lifecycle.evidence.title',
      urlParams: ['article', 'market', 'month', 'transitionRunId'],
      component: () => import('./lifecycle/TransitionScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.evidence', formats: ['csv', 'xlsx'], resolve: lifecycleEvidenceExport },
      drill: {
        terminal: true,
        reason: 'One recommendation — one article, one market, one effective month — with every signal read and every door holding it, is the finest grain GET /exit/lifecycle-transitions/{recommendation_id} publishes; the lines its acceptance sets off are on the withdrawal page.',
      },
    },
    {
      // OUTLET EXIT — the month's ZONA_SALES → OUTLET recommendations for one market (bata-reqs
      // issue 35 P4, `docs/adr/7004`; bata-platform ADR 7028). Market and month on the link,
      // neither defaulted; one run of the month optional. No run for the month is a designed state.
      path: 'lifecycle-outlet',
      titleKey: 'exit.lifecycle.outlet.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/OutletScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.outlet', formats: ['csv', 'xlsx'], resolve: lifecycleOutletExport },
      drill: {
        to: '/exit/lifecycle-outlet/:recommendationId',
        grain: 'article',
        alsoTo: ['/exit/lifecycle-outlet-transfers'],
      },
    },
    {
      // TRANSFER TO THE OUTLET — what the month's transfer did with each accepted move (ADR 7028).
      path: 'lifecycle-outlet-transfers',
      titleKey: 'exit.lifecycle.outletTransfers.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/OutletTransfersScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.outletTransfers', formats: ['csv', 'xlsx'], resolve: lifecycleOutletTransfersExport },
      drill: {
        terminal: true,
        reason: 'An accepted move opens on this page (?article=): every door the transfer named and its lane-book lines, one per source, destination and size, which is the finest grain GET /exit/consolidation/lines publishes; the lines are decided here with the exit lane decision.',
      },
    },
    {
      // THE PHASE REGISTER — every article's lifecycle phase in force for one market and month, with
      // its source, the minimum-age guard, the entry month, the inference beside a decision, the
      // exclusion from the headline promotions and the next planned move (bata-reqs issue 35 P5,
      // `docs/adr/7005`; bata-platform ADR 7031). Market and month on the link, neither defaulted; no run.
      path: 'lifecycle-phases',
      titleKey: 'exit.lifecycle.phases.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/PhasesScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.phases', formats: ['csv', 'xlsx'], resolve: lifecyclePhasesExport },
      drill: { to: '/exit/lifecycle-phases/:articleKey', grain: 'article' },
    },
    {
      // One article's phase month by month, its decisions (superseded ones marked) and the
      // inference's own rows. Reached from the register's rows.
      path: 'lifecycle-phases/:articleKey',
      titleKey: 'exit.lifecycle.phaseHistory.title',
      urlParams: ['article', 'market', 'month', 'outletRunId', 'outletTransferRunId', 'transitionRunId', 'withdrawalRunId'],
      component: () => import('./lifecycle/PhaseHistoryScreen'),
      surfaces: ['table'],
      exports: { id: 'exit.lifecycle.phaseHistory', formats: ['csv', 'xlsx'], resolve: lifecyclePhaseHistoryExport },
      drill: {
        terminal: true,
        reason: 'One article in one market, month by month, with every decision and every inferred row, is the finest grain GET /exit/lifecycle-phases/{article_key}/history publishes; a decision links to the recommendation it accepted.',
      },
    },
    {
      // One outlet-exit recommendation: its entry price, the comparison, the motion, the readings,
      // every door, and the planner's answer. Reached from the outlet page's rows.
      path: 'lifecycle-outlet/:recommendationId',
      titleKey: 'exit.lifecycle.outlet.evidenceTitle',
      urlParams: ['article', 'market', 'month', 'outletRunId'],
      component: () => import('./lifecycle/OutletExitScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'exit.lifecycle.outletEvidence', formats: ['csv', 'xlsx'], resolve: lifecycleOutletEvidenceExport },
      drill: {
        terminal: true,
        reason: 'One outlet-exit recommendation — one article, one market, one effective month — with both readings and every door in the move is the finest grain GET /exit/outlet-exits/{recommendation_id} publishes; the lines its acceptance sets off are on the transfer page.',
      },
    },
  ],
});
